import { logEvent as firebaseLogEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { analytics, analyticsReady } from '../firebase';

// =============================================================================
// CONVERSION FUNNEL - Aligned to Value Proposition
// =============================================================================
// IndexAlpha Value: Institutional-grade stock research, portfolio risk analysis, and analytical insights
//
// Funnel Stages:
// 1. AWARENESS  - User lands on site (page_view on /)
// 2. DISCOVERY  - User explores free tools (stock research, chat)
// 3. ENGAGEMENT - User gets value (views charts, gets AI answers)
// 4. ACTIVATION - User signs up to save work (portfolios, watchlists)
// 5. RETENTION  - User returns and uses advanced features
// =============================================================================

// Event names for tracking user funnel
export const ANALYTICS_EVENTS = {
  // Page views
  PAGE_VIEW: 'page_view',

  // Authentication events
  LOGIN_DIALOG_OPENED: 'login_dialog_opened',
  LOGIN_DIALOG_CLOSED: 'login_dialog_closed',
  SIGN_IN_STARTED: 'sign_in_started',
  SIGN_IN_SUCCESS: 'sign_in_success',
  SIGN_IN_ERROR: 'sign_in_error',
  SIGN_UP_SUCCESS: 'sign_up_success',
  SIGN_OUT: 'sign_out',

  // Agent interactions
  AGENT_CLICKED: 'agent_clicked',
  AGENT_VIEWED: 'agent_viewed',
  AGENT_REPORT_VIEWED: 'agent_report_viewed',
  AGENT_LOCKED_CLICKED: 'agent_locked_clicked',

  // Feature usage
  WATCHLIST_ADDED: 'watchlist_added',
  WATCHLIST_REMOVED: 'watchlist_removed',
  ARCHIVE_SEARCHED: 'archive_searched',
  ACADEMY_SEARCHED: 'academy_searched',
  FAQ_VIEWED: 'faq_viewed',

  // Subscription events
  SUBSCRIPTION_MODAL_OPENED: 'subscription_modal_opened',
  SUBSCRIPTION_PLAN_SELECTED: 'subscription_plan_selected',
  SUBSCRIPTION_STARTED: 'subscription_started',

  // Paid funnel (ROADMAP 0.10) — the /plans → checkout → /billing → provisioned
  // chain, plus the cancel loop. `begin_checkout` and `purchase` are GA4's
  // RECOMMENDED commerce names (mark both as key events in the console).
  PLANS_PAGE_VIEWED: 'plans_page_viewed',
  PAYWALL_MOMENT: 'paywall_moment',
  // The free daily cap as a legible wall (ROADMAP 0.R R5): the counter first
  // shown that day, and the wall reached.
  DAILY_CAP_SEEN: 'daily_cap_seen',
  DAILY_CAP_HIT: 'daily_cap_hit',
  TRIAL_CODE_REDEEMED: 'trial_code_redeemed',
  TRIAL_ENDED_VIEWED: 'trial_ended_viewed',
  NEWSLETTER_CTA: 'newsletter_cta',
  PAID_FEATURE_USED: 'paid_feature_used',
  BEGIN_CHECKOUT: 'begin_checkout',
  CHECKOUT_RETURNED: 'checkout_returned',
  PURCHASE: 'purchase',
  BILLING_PORTAL_OPENED: 'billing_portal_opened',
  SUBSCRIPTION_CANCEL_STARTED: 'subscription_cancel_started',
  SUBSCRIPTION_CANCEL_CONFIRMED: 'subscription_cancel_confirmed',
  SUBSCRIPTION_CANCEL_KEPT: 'subscription_cancel_kept',
  SUBSCRIPTION_CANCEL_RESUMED: 'subscription_cancel_resumed',
  SUBSCRIPTION_DOWNGRADE_STARTED: 'subscription_downgrade_started',
  SUBSCRIPTION_DOWNGRADE_CONFIRMED: 'subscription_downgrade_confirmed',
  SUBSCRIPTION_DOWNGRADE_KEPT: 'subscription_downgrade_kept',

  // Navigation
  SIDEBAR_OPENED: 'sidebar_opened',
  NAV_ITEM_CLICKED: 'nav_item_clicked',

  // =============================================================================
  // FUNNEL EVENTS - Track user journey to value
  // =============================================================================

  // Stage 1: AWARENESS - First touch
  FUNNEL_LANDING: 'funnel_landing',
  FUNNEL_REFERRAL_SOURCE: 'funnel_referral_source',

  // Stage 2: DISCOVERY - Exploring tools
  FUNNEL_TOOL_DISCOVERED: 'funnel_tool_discovered',
  FUNNEL_STOCK_RESEARCH_STARTED: 'funnel_stock_research_started',
  FUNNEL_CHAT_STARTED: 'funnel_chat_started',
  FUNNEL_PORTFOLIO_STARTED: 'funnel_portfolio_started',

  // Stage 3: ENGAGEMENT - Value delivered
  FUNNEL_ENGAGED: 'funnel_engaged',
  FUNNEL_VALUE_DELIVERED: 'funnel_value_delivered',
  FUNNEL_CHART_GENERATED: 'funnel_chart_generated',
  FUNNEL_AI_ANSWER_RECEIVED: 'funnel_ai_answer_received',
  // The reader took a light answer's offer of the deeper run (item 4h). The
  // offer being SHOWN has no event of its own — it is inferable from the
  // answer, and a second event would be bloat.
  FUNNEL_FULL_ANALYSIS_TAKEN: 'funnel_full_analysis_taken',
  FUNNEL_RISK_ANALYSIS_COMPLETE: 'funnel_risk_analysis_complete',

  // Stage 4: ACTIVATION - Sign up to save work
  FUNNEL_SIGN_IN_PROMPT: 'funnel_sign_in_prompt',
  FUNNEL_SIGN_IN_COMPLETED: 'funnel_sign_in_completed',
  FUNNEL_FIRST_SAVE: 'funnel_first_save',
  FUNNEL_FIRST_AGENT_VIEW: 'funnel_first_agent_view',

  // Stage 5: RETENTION - Return visits
  FUNNEL_RETURN_VISIT: 'funnel_return_visit',
  FUNNEL_FEATURE_ADOPTED: 'funnel_feature_adopted',

  // Promotion page specific events
  PROMO_PAGE_VIEWED: 'promo_page_viewed',
  PROMO_CTA_CLICKED: 'promo_cta_clicked',
  ROI_CALCULATOR_USED: 'roi_calculator_used',
  TESTIMONIAL_VIEWED: 'testimonial_viewed',
  FEATURE_COMPARISON_VIEWED: 'feature_comparison_viewed',
  TRIAL_STARTED: 'trial_started',
  CONVERSION_COMPLETED: 'conversion_completed',

  // =============================================================================
  // STOCK RESEARCH EVENTS
  // =============================================================================
  STOCK_SEARCH: 'stock_search',
  STOCK_VIEWED: 'stock_viewed',
  STOCK_METRICS_EXPANDED: 'stock_metrics_expanded',
  STOCK_NEWS_CLICKED: 'stock_news_clicked',
  STOCK_CHART_INTERACTED: 'stock_chart_interacted',

  // =============================================================================
  // CHAT EVENTS - AI conversation tracking
  // =============================================================================
  CHAT_SESSION_STARTED: 'chat_session_started',
  CHAT_MESSAGE_SENT: 'chat_message_sent',
  CHAT_RESPONSE_RECEIVED: 'chat_response_received',
  CHAT_CHART_VIEWED: 'chat_chart_viewed',
  CHAT_CHART_EXPORTED: 'chat_chart_exported',
  CHAT_FILE_UPLOADED: 'chat_file_uploaded',
  CHAT_PRESENTATION_EXPORTED: 'chat_presentation_exported',
  CHAT_SESSION_SHARED: 'chat_session_shared',
  CHAT_SESSION_DELETED: 'chat_session_deleted',
  /**
   * The first source row appearing while an answer is still being written
   * (ROADMAP item 13d). **Time to first meaningful pixel is the number that
   * item exists to move**, and nothing measured it before — the reader used to
   * watch task machinery and then receive everything at once.
   */
  CHAT_FIRST_SOURCE_SHOWN: 'chat_first_source_shown',

  // =============================================================================
  // KNOWLEDGE BASE EVENTS
  // =============================================================================
  KNOWLEDGE_BASE_UPLOAD: 'knowledge_base_upload',
  KNOWLEDGE_BASE_DELETE: 'knowledge_base_delete',
  KNOWLEDGE_BASE_REFERENCED: 'knowledge_base_referenced',

  // =============================================================================
  // PORTFOLIO EVENTS
  // =============================================================================
  PORTFOLIO_SUBMITTED: 'portfolio_submitted',
  PORTFOLIO_ANALYSIS_VIEWED: 'portfolio_analysis_viewed',
  PORTFOLIO_SAVED: 'portfolio_saved',
  PORTFOLIO_UPDATED: 'portfolio_updated',
  PORTFOLIO_DELETED: 'portfolio_deleted',
  PORTFOLIO_IMPORTED: 'portfolio_imported',

  // Risk report events
  RISK_REPORT_SAVED: 'risk_report_saved',
  RISK_REPORT_LOADED: 'risk_report_loaded',
  RISK_REPORT_DELETED: 'risk_report_deleted',

  // =============================================================================
  // WATCHLIST EVENTS
  // =============================================================================
  WATCHLIST_CREATED: 'watchlist_created',
  WATCHLIST_SYMBOL_ADDED: 'watchlist_symbol_added',
  WATCHLIST_SYMBOL_REMOVED: 'watchlist_symbol_removed',
  WATCHLIST_DELETED: 'watchlist_deleted',

  // Theme/Settings events
  THEME_TOGGLED: 'theme_toggled',
  SETTING_CHANGED: 'setting_changed',

  // Error tracking events
  ERROR_OCCURRED: 'error_occurred',
  API_FAILURE: 'api_failure',

  // =============================================================================
  // ONBOARDING FUNNEL EVENTS - Discovery Quiz & Task Completion
  // =============================================================================

  // Discovery Quiz Flow
  ONBOARDING_QUIZ_OPENED: 'onboarding_quiz_opened',
  ONBOARDING_QUIZ_STEP_VIEWED: 'onboarding_quiz_step_viewed',
  ONBOARDING_QUIZ_STEP_COMPLETED: 'onboarding_quiz_step_completed',
  ONBOARDING_QUIZ_COMPLETED: 'onboarding_quiz_completed',
  ONBOARDING_QUIZ_ABANDONED: 'onboarding_quiz_abandoned',
  ONBOARDING_QUIZ_RESULT_VIEWED: 'onboarding_quiz_result_viewed',
  ONBOARDING_QUIZ_INSIGHTS_SHOWN: 'onboarding_quiz_insights_shown',
  ONBOARDING_QUIZ_INSIGHT_CLICKED: 'onboarding_quiz_insight_clicked',
  ONBOARDING_QUIZ_CTA_CLICKED: 'onboarding_quiz_cta_clicked',

  // Post-Login Welcome Flow
  ONBOARDING_WELCOME_SHOWN: 'onboarding_welcome_shown',
  ONBOARDING_WELCOME_CTA_CLICKED: 'onboarding_welcome_cta_clicked',

  // Gamified Task Completion
  ONBOARDING_TASK_COMPLETED: 'onboarding_task_completed',
  ONBOARDING_CHECKLIST_SHOWN: 'onboarding_checklist_shown',
  ONBOARDING_CHECKLIST_DISMISSED: 'onboarding_checklist_dismissed',
  ONBOARDING_ALL_STARS_EARNED: 'onboarding_all_stars_earned',

  // Funnel Drop-off Tracking
  ONBOARDING_DROPOFF: 'onboarding_dropoff',

  // =============================================================================
  // LEVEL-UP GAMIFICATION EVENTS
  // =============================================================================
  LEVEL_UP_QUIZ_STARTED: 'level_up_quiz_started',
  LEVEL_UP_QUIZ_COMPLETED: 'level_up_quiz_completed',
  LEVEL_UP_QUIZ_PASSED: 'level_up_quiz_passed',
  LEVEL_UP_QUIZ_FAILED: 'level_up_quiz_failed',
  LEVEL_UP_CHALLENGE_STARTED: 'level_up_challenge_started',
  LEVEL_UP_CHALLENGE_COMPLETED: 'level_up_challenge_completed',
  LEVEL_UP_LEVEL_ACHIEVED: 'level_up_level_achieved',
  LEVEL_UP_DISCOUNT_SHOWN: 'level_up_discount_shown',
  NOTIFICATION_OPENED: 'notification_opened',
  NOTIFICATION_READ: 'notification_read',

  // =============================================================================
  // MICRO-INTERACTION EVENTS - Granular user behavior tracking
  // =============================================================================
  MODULE_EXPANDED: 'module_expanded',
  MODULE_COLLAPSED: 'module_collapsed',
  TAB_SELECTED: 'tab_selected',
  DROPDOWN_SELECTED: 'dropdown_selected',
  CHART_INTERACTION: 'chart_interaction',
  TABLE_SORTED: 'table_sorted',
  TABLE_FILTERED: 'table_filtered',
  MODAL_OPENED: 'modal_opened',
  MODAL_CLOSED: 'modal_closed',
  SCROLL_DEPTH: 'scroll_depth',
  QUICK_ACTION: 'quick_action',
  PAGE_ENGAGEMENT: 'page_engagement',
};

// Sources for tracking where actions originated
export const ANALYTICS_SOURCES = {
  NAVBAR: 'navbar',
  SIDEBAR: 'sidebar',
  NAVIGATION_RAIL: 'navigation_rail',
  PHONE_TABS: 'phone_tabs',
  PHONE_MENU: 'phone_menu',
  XAVIER_LOGIN_PROMPT: 'xavier_login_prompt',
  APOLLO_LOGIN_PROMPT: 'apollo_login_prompt',
  KELLY_LOGIN_PROMPT: 'kelly_login_prompt',
  CHARLES_LOGIN_PROMPT: 'charles_login_prompt',
  BRADFORD_LOGIN_PROMPT: 'bradford_login_prompt',
  JERRY_PAGE: 'jerry_page',
  XAVIER_PAGE: 'xavier_page',
  APOLLO_PAGE: 'apollo_page',
  KELLY_PAGE: 'kelly_page',
  CHARLES_PAGE: 'charles_page',
  BRADFORD_PAGE: 'bradford_page',
  NEWS_PAGE: 'news_page',
  NIKKI_PAGE: 'nikki_page',
  LANDING_PAGE: 'landing_page',
  SUBSCRIPTION_MODAL: 'subscription_modal',
  PROFILE_PAGE: 'profile_page',
  DIRECT_URL: 'direct_url',
  SHARED_REPORT: 'shared_report',   // a signed-out reader on a report someone sent them ([[BUGS #295]])
  PROMO_PAGE: 'promo_page',
  PROMO_PAGE_PRIMARY: 'promo_page_primary_cta',
  PROMO_PAGE_SECONDARY: 'promo_page_secondary_cta',
  ROI_CALCULATOR: 'roi_calculator',
  FEATURE_COMPARISON: 'feature_comparison',
  TESTIMONIAL_SECTION: 'testimonial_section',
  PORTFOLIO_ANALYSIS_PAGE: 'portfolio_analysis_page',
  STOCK_RESEARCH_PAGE: 'stock_research_page',
  INVESTMENT_ALIGNMENT: 'investment_alignment_cta',
};

// ============================================================================
// Closed unions for activation + feature-repeat-use, so the Firebase Analytics
// user-property cap (25 props/project) cannot be silently breached by a typo
// or by a downstream caller passing an ad-hoc string.
// ============================================================================
export type ActivationType =
  | 'first_stock_research_engaged'
  | 'first_chat_chart_received'
  | 'first_real_risk_analysis'
  | 'first_saved_portfolio_5plus'
  | 'first_watchlist_5plus';

/** Features tracked for habit-formation (repeat-use) analytics. Closed union
 *  keeps the `habits_formed` CSV bounded so the user-property cap is safe. */
export type FeatureName = 'stock_research' | 'risk_analysis' | 'chat';

/** Snake-cased GA4 user-property keys for `refreshUserProperties`. Callers
 *  map their store fields explicitly — no opaque regex conversion in the
 *  service. Add a field here only when you also want the cohort dimension
 *  in BigQuery. */
export interface UserPropertiesRefresh {
  subscription_tier?: string;          // 'starter' | 'investor' | 'team' | 'enterprise'
  portfolios_count?: number;
  watchlists_count?: number;
  watchlist_symbols_total?: number;
  level_up_tier?: number;
  chat_sessions_total?: number;
  features_used_count?: number;
  is_power_user?: boolean;
  last_active_at?: string;             // ISO date (YYYY-MM-DD); defaults to today if absent
}

class AnalyticsService {
  private userId: string | null = null;

  /** Events fired before the SDK resolves ([[BUGS #140]]). The landing
   *  page_view — the funnel's first step — is the usual casualty. Capped so a
   *  browser where analytics never initialises cannot grow it unbounded. */
  private pending: Array<{ name: string; params?: Record<string, any> }> = [];
  private static readonly PENDING_CAP = 50;

  /** Live readiness, not a snapshot taken at import time — the snapshot is
   *  what silently dropped every event this product ever tracked. */
  private get isInitialized(): boolean {
    return Boolean(analytics);
  }

  constructor() {
    // Promise.resolve tolerates a partially-mocked ../firebase (tests) and
    // costs nothing in production.
    Promise.resolve(analyticsReady).then(instance => {
      if (!instance) return;                     // unsupported browser: stay silent
      // A sign-in restored before analytics started set no User-ID, and
      // nothing retried it — GA could not match visitors to accounts
      // ([[BUGS #527]]). Apply it before the queued events go out.
      if (this.userId) {
        try {
          setUserId(instance, this.userId);
        } catch (error) {
          console.error('[Analytics] Error setting user ID:', error);
        }
      }
      const queued = this.pending.splice(0);
      queued.forEach(e => this.logEvent(e.name, e.params));
    });
  }

  /**
   * Log a custom event to Firebase Analytics
   */
  logEvent(eventName: string, parameters?: Record<string, any>) {
    if (!analytics) {
      // Not (yet) initialised — hold the event rather than drop it.
      if (this.pending.length < AnalyticsService.PENDING_CAP) {
        this.pending.push({ name: eventName, params: parameters });
      }
      return;
    }

    try {
      firebaseLogEvent(analytics, eventName, {
        ...parameters,
        timestamp: new Date().toISOString(),
        user_id: this.userId,
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[Analytics] Event logged:', eventName, parameters);
      }
    } catch (error) {
      console.error('[Analytics] Error logging event:', error);
    }
  }

  /**
   * Track page views
   */
  trackPageView(pageName: string, pageLocation?: string) {
    this.logEvent(ANALYTICS_EVENTS.PAGE_VIEW, {
      page_name: pageName,
      page_location: pageLocation || window.location.pathname,
      page_title: document.title,
    });
  }

  /**
   * Track login dialog interactions
   */
  trackLoginDialog(action: 'open' | 'close', source: string) {
    const eventName = action === 'open' 
      ? ANALYTICS_EVENTS.LOGIN_DIALOG_OPENED 
      : ANALYTICS_EVENTS.LOGIN_DIALOG_CLOSED;
    
    this.logEvent(eventName, {
      source,
      page_location: window.location.pathname,
    });

    // Track funnel step when login prompt is shown
    if (action === 'open') {
      this.logEvent(ANALYTICS_EVENTS.FUNNEL_SIGN_IN_PROMPT, {
        source,
        trigger_location: window.location.pathname,
      });
    }
  }

  /**
   * Track sign-in attempts and results
   */
  trackSignIn(status: 'started' | 'success' | 'error', method: string, source: string, error?: string) {
    let eventName: string;
    switch (status) {
      case 'started':
        eventName = ANALYTICS_EVENTS.SIGN_IN_STARTED;
        break;
      case 'success':
        eventName = ANALYTICS_EVENTS.SIGN_IN_SUCCESS;
        break;
      case 'error':
        eventName = ANALYTICS_EVENTS.SIGN_IN_ERROR;
        break;
    }

    this.logEvent(eventName, {
      method,
      source,
      error: error || undefined,
      page_location: window.location.pathname,
    });

    // Track funnel completion on successful sign-in
    if (status === 'success') {
      this.logEvent(ANALYTICS_EVENTS.FUNNEL_SIGN_IN_COMPLETED, {
        method,
        source,
        conversion_location: window.location.pathname,
      });
    }
  }

  /**
   * Track new user sign-ups (first-time sign-ins)
   */
  trackSignUp(method: string, source: string, email?: string) {
    this.logEvent(ANALYTICS_EVENTS.SIGN_UP_SUCCESS, {
      method,
      source,
      email_domain: email ? email.split('@')[1] : undefined,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track sign-out
   */
  trackSignOut(source: string) {
    this.logEvent(ANALYTICS_EVENTS.SIGN_OUT, {
      source,
      page_location: window.location.pathname,
    });
    this.setUserId(null);
  }

  /**
   * Track agent interactions including promotion page interactions
   */
  trackAgentInteraction(
    action: 'clicked' | 'viewed' | 'report_viewed' | 'locked_clicked' | 'viewed_promotion' | 'viewed_subscription_promo' | 'clicked_signin' | 'clicked_upgrade' | 'tried_jerry_instead' | 'tried_free_instead',
    agentName: string,
    source: string
  ) {
    let eventName: string;
    switch (action) {
      case 'clicked':
        eventName = ANALYTICS_EVENTS.AGENT_CLICKED;
        break;
      case 'viewed':
        eventName = ANALYTICS_EVENTS.AGENT_VIEWED;
        break;
      case 'report_viewed':
        eventName = ANALYTICS_EVENTS.AGENT_REPORT_VIEWED;
        break;
      case 'locked_clicked':
        eventName = ANALYTICS_EVENTS.AGENT_LOCKED_CLICKED;
        break;
      case 'viewed_promotion':
        eventName = ANALYTICS_EVENTS.PROMO_PAGE_VIEWED;
        break;
      case 'viewed_subscription_promo':
        eventName = ANALYTICS_EVENTS.PROMO_PAGE_VIEWED;
        break;
      case 'clicked_signin':
        eventName = ANALYTICS_EVENTS.PROMO_CTA_CLICKED;
        break;
      case 'clicked_upgrade':
        eventName = ANALYTICS_EVENTS.PROMO_CTA_CLICKED;
        break;
      case 'tried_jerry_instead':
        eventName = ANALYTICS_EVENTS.PROMO_CTA_CLICKED;
        break;
      case 'tried_free_instead':
        eventName = ANALYTICS_EVENTS.PROMO_CTA_CLICKED;
        break;
    }

    this.logEvent(eventName, {
      agent_name: agentName,
      source,
      page_location: window.location.pathname,
    });

    // Track first agent view for funnel
    if (action === 'viewed') {
      const viewedAgents = localStorage.getItem('viewed_agents') || '[]';
      const agents = JSON.parse(viewedAgents);
      if (!agents.includes(agentName)) {
        agents.push(agentName);
        localStorage.setItem('viewed_agents', JSON.stringify(agents));
        
        if (agents.length === 1) {
          this.logEvent(ANALYTICS_EVENTS.FUNNEL_FIRST_AGENT_VIEW, {
            agent_name: agentName,
            source,
          });
        }
      }
    }
  }

  /**
   * Track subscription modal interactions
   */
  trackSubscriptionModal(action: 'opened' | 'plan_selected' | 'started', planName?: string) {
    let eventName: string;
    switch (action) {
      case 'opened':
        eventName = ANALYTICS_EVENTS.SUBSCRIPTION_MODAL_OPENED;
        break;
      case 'plan_selected':
        eventName = ANALYTICS_EVENTS.SUBSCRIPTION_PLAN_SELECTED;
        break;
      case 'started':
        eventName = ANALYTICS_EVENTS.SUBSCRIPTION_STARTED;
        break;
    }

    this.logEvent(eventName, {
      plan_name: planName,
      page_location: window.location.pathname,
    });
  }

  // ---------------------------------------------------------------------------
  // Paid funnel (ROADMAP 0.10). Display prices, for telemetry value only —
  // Stripe owns real billing and the webhook owns entitlements.
  // ---------------------------------------------------------------------------
  private static readonly PLAN_VALUE_USD: Record<string, number> = {
    signals: 100,
    strategist: 200,
  };

  private planItems(plan: string) {
    return {
      currency: 'USD',
      value: AnalyticsService.PLAN_VALUE_USD[plan] ?? 0,
      items: [{ item_id: plan, item_name: `IndexAlpha ${plan}`, price: AnalyticsService.PLAN_VALUE_USD[plan] ?? 0, quantity: 1 }],
    };
  }

  /** Retention's leading indicator (ROADMAP 0.10 follow-up): which paid
   *  features actually get used. A subscriber whose usage goes quiet here is
   *  the one the cancel events will describe later. */
  trackPaidFeatureUsed(feature: string, detail?: Record<string, any>) {
    this.logEvent(ANALYTICS_EVENTS.PAID_FEATURE_USED, { feature, ...detail });
  }

  /** The deferred paywall after a manual risk result — the highest-intent
   *  moment and the funnel's first paid step. 'shown' counts exposures,
   *  'clicked' the pull; the gap between them is the drop-off. */
  trackPaywallMoment(action: 'shown' | 'clicked', plan?: string, extra: Record<string, string> = {}) {
    // `code` names the refusal that raised it (ROADMAP 0.R R2/R12) — which
    // wall readers hit, not only that one was hit.
    this.logEvent(ANALYTICS_EVENTS.PAYWALL_MOMENT, { action, plan, ...extra });
  }

  /** The daily cap: `seen` — the counter shown, once a day; `hit` — the wall reached (ROADMAP 0.R R5). */
  trackDailyCap(kind: 'seen' | 'hit', params: Record<string, string | number> = {}) {
    this.logEvent(kind === 'seen' ? ANALYTICS_EVENTS.DAILY_CAP_SEEN : ANALYTICS_EVENTS.DAILY_CAP_HIT, params);
  }

  /** A trial code redeemed, or an ended trial's "add your billing details"
   *  seen (ROADMAP 0.R R6) — the no-card leg of the funnel. */
  trackTrial(kind: 'redeemed' | 'ended_viewed', plan: string) {
    this.logEvent(kind === 'redeemed' ? ANALYTICS_EVENTS.TRIAL_CODE_REDEEMED : ANALYTICS_EVENTS.TRIAL_ENDED_VIEWED, { plan });
  }

  /** The newsletter box (ROADMAP 0.R R9, sign-in version 2026-10-19): a
   *  signed-out reader chose to sign in for the Brief, or a signed-in reader who
   *  had turned it off turned it back on. */
  trackNewsletterCta(action: 'signin_clicked' | 'turned_on', source: string) {
    this.logEvent(ANALYTICS_EVENTS.NEWSLETTER_CTA, { action, source });
  }

  /** The plans page rendered — the top of the paid funnel. */
  trackPlansViewed(currentPlan: string) {
    this.logEvent(ANALYTICS_EVENTS.PLANS_PAGE_VIEWED, { current_plan: currentPlan });
  }

  /** Upgrade clicked; the browser is about to leave for Stripe checkout. */
  trackCheckoutStarted(plan: string) {
    this.logEvent(ANALYTICS_EVENTS.BEGIN_CHECKOUT, this.planItems(plan));
  }

  /** Landed back on /billing. `outcome` is what the page could tell:
   *  'pending' (session_id, plan not yet provisioned), 'active', or
   *  'abandoned' (bare return from a cancelled checkout). */
  trackCheckoutReturned(outcome: 'pending' | 'active' | 'abandoned') {
    this.logEvent(ANALYTICS_EVENTS.CHECKOUT_RETURNED, { outcome });
  }

  /** The conversion: the plan flipped active on /billing after a checkout.
   *  transaction_id is the Stripe checkout session id, so GA4 deduplicates
   *  refresh/revisit fires of the same purchase. */
  trackPurchaseConfirmed(plan: string, sessionId: string) {
    this.logEvent(ANALYTICS_EVENTS.PURCHASE, {
      ...this.planItems(plan),
      transaction_id: sessionId,
    });
  }

  trackBillingPortalOpened(plan: string) {
    this.logEvent(ANALYTICS_EVENTS.BILLING_PORTAL_OPENED, { current_plan: plan });
  }

  /** The cancel loop on /plans: started → confirmed | kept; resumed undoes a
   *  pending cancellation later. Churn intent is a conversion signal too. */
  /** The downgrade loop (0.11): a softer churn signal than cancel — the
   *  reader still pays, just less. 'kept' covers both backing out of the
   *  confirm and undoing a scheduled switch. */
  trackDowngradeFlow(step: 'started' | 'confirmed' | 'kept', from: string) {
    const name = {
      started: ANALYTICS_EVENTS.SUBSCRIPTION_DOWNGRADE_STARTED,
      confirmed: ANALYTICS_EVENTS.SUBSCRIPTION_DOWNGRADE_CONFIRMED,
      kept: ANALYTICS_EVENTS.SUBSCRIPTION_DOWNGRADE_KEPT,
    }[step];
    this.logEvent(name, { from, to: 'signals' });
  }

  trackCancelFlow(step: 'started' | 'confirmed' | 'kept' | 'resumed', plan: string) {
    const name = {
      started: ANALYTICS_EVENTS.SUBSCRIPTION_CANCEL_STARTED,
      confirmed: ANALYTICS_EVENTS.SUBSCRIPTION_CANCEL_CONFIRMED,
      kept: ANALYTICS_EVENTS.SUBSCRIPTION_CANCEL_KEPT,
      resumed: ANALYTICS_EVENTS.SUBSCRIPTION_CANCEL_RESUMED,
    }[step];
    this.logEvent(name, { plan });
  }

  /**
   * Track subscription conversion funnel
   */
  trackSubscriptionConversion(
    action: 'started' | 'trial_started' | 'completed' | 'abandoned',
    planName: string,
    agentName?: string,
    value?: number
  ) {
    let eventName: string;
    switch (action) {
      case 'started':
        eventName = ANALYTICS_EVENTS.SUBSCRIPTION_STARTED;
        break;
      case 'trial_started':
        eventName = ANALYTICS_EVENTS.TRIAL_STARTED;
        break;
      case 'completed':
        eventName = ANALYTICS_EVENTS.CONVERSION_COMPLETED;
        break;
      case 'abandoned':
        eventName = 'conversion_abandoned';
        break;
    }

    this.logEvent(eventName, {
      plan_name: planName,
      agent_name: agentName,
      value: value,
      page_location: window.location.pathname,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track ROI calculator usage
   */
  trackROICalculator(agentName: string, portfolioAmount: number, calculatedROI: number) {
    this.logEvent(ANALYTICS_EVENTS.ROI_CALCULATOR_USED, {
      agent_name: agentName,
      portfolio_amount: portfolioAmount,
      calculated_roi: calculatedROI,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track navigation interactions
   */
  trackNavigation(item: string, source: string) {
    this.logEvent(ANALYTICS_EVENTS.NAV_ITEM_CLICKED, {
      item_name: item,
      source,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track watchlist interactions
   */
  trackWatchlist(action: 'added' | 'removed', symbol: string) {
    const eventName = action === 'added' 
      ? ANALYTICS_EVENTS.WATCHLIST_ADDED 
      : ANALYTICS_EVENTS.WATCHLIST_REMOVED;
    
    this.logEvent(eventName, {
      symbol,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track search actions
   */
  trackSearch(type: 'archive' | 'academy', query: string, resultsCount?: number) {
    const eventName = type === 'archive' 
      ? ANALYTICS_EVENTS.ARCHIVE_SEARCHED 
      : ANALYTICS_EVENTS.ACADEMY_SEARCHED;
    
    this.logEvent(eventName, {
      search_query: query,
      results_count: resultsCount,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track user engagement milestones
   */
  trackEngagement(milestone: 'first_visit' | 'return_visit' | 'engaged_user') {
    this.logEvent(ANALYTICS_EVENTS.FUNNEL_ENGAGED, {
      milestone,
      page_location: window.location.pathname,
    });
  }

  /**
   * Set user ID for all future events
   */
  setUserId(userId: string | null) {
    this.userId = userId;
    if (this.isInitialized && analytics && userId) {
      try {
        setUserId(analytics, userId);
      } catch (error) {
        console.error('[Analytics] Error setting user ID:', error);
      }
    }
  }

  /**
   * Set user properties for segmentation
   */
  setUserProperties(properties: Record<string, any>) {
    if (this.isInitialized && analytics) {
      try {
        setUserProperties(analytics, properties);
      } catch (error) {
        console.error('[Analytics] Error setting user properties:', error);
      }
    }
  }

  /**
   * Track conversion funnel step
   */
  trackFunnelStep(step: 'landing' | 'engaged' | 'sign_in_prompt' | 'signed_in' | 'first_agent', metadata?: Record<string, any>) {
    const eventMap = {
      landing: ANALYTICS_EVENTS.FUNNEL_LANDING,
      engaged: ANALYTICS_EVENTS.FUNNEL_ENGAGED,
      sign_in_prompt: ANALYTICS_EVENTS.FUNNEL_SIGN_IN_PROMPT,
      signed_in: ANALYTICS_EVENTS.FUNNEL_SIGN_IN_COMPLETED,
      first_agent: ANALYTICS_EVENTS.FUNNEL_FIRST_AGENT_VIEW,
    };

    this.logEvent(eventMap[step], {
      ...metadata,
      page_location: window.location.pathname,
      referrer: document.referrer,
    });
  }

  /**
   * Track stock search
   */
  trackStockSearch(query: string, resultCount: number, source: string) {
    this.logEvent(ANALYTICS_EVENTS.STOCK_SEARCH, {
      search_query: query,
      result_count: resultCount,
      source,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track stock viewed. Also rolls up to funnel_value_delivered (once per
   * session) since a stock view IS the value moment for the research path.
   */
  trackStockViewed(symbol: string, source: string) {
    this.logEvent(ANALYTICS_EVENTS.STOCK_VIEWED, {
      symbol,
      source,
      page_location: window.location.pathname,
    });
    this.trackValueDelivered('stock_data', { symbol, source });
  }

  /**
   * Track portfolio submission
   */
  trackPortfolioSubmitted(method: 'manual' | 'import' | 'sample' | 'screenshot', symbolCount: number) {
    this.logEvent(ANALYTICS_EVENTS.PORTFOLIO_SUBMITTED, {
      submission_method: method,
      symbol_count: symbolCount,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track portfolio analysis viewed
   */
  trackPortfolioAnalysisViewed(portfolioId: string, riskScore?: number) {
    this.logEvent(ANALYTICS_EVENTS.PORTFOLIO_ANALYSIS_VIEWED, {
      portfolio_id: portfolioId,
      risk_score: riskScore,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track theme toggle
   */
  trackThemeToggled(fromMode: string, toMode: string) {
    this.logEvent(ANALYTICS_EVENTS.THEME_TOGGLED, {
      from_mode: fromMode,
      to_mode: toMode,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track setting changed
   */
  trackSettingChanged(setting: string, value: any) {
    this.logEvent(ANALYTICS_EVENTS.SETTING_CHANGED, {
      setting_name: setting,
      setting_value: value,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track error occurrence
   */
  trackError(errorType: string, errorMessage: string, context: string) {
    this.logEvent(ANALYTICS_EVENTS.ERROR_OCCURRED, {
      error_type: errorType,
      error_message: errorMessage,
      context,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track API failure
   */
  trackAPIFailure(endpoint: string, statusCode: number, error: string) {
    this.logEvent(ANALYTICS_EVENTS.API_FAILURE, {
      endpoint,
      status_code: statusCode,
      error_message: error,
      page_location: window.location.pathname,
    });
  }

  // =============================================================================
  // CHAT TRACKING METHODS
  // =============================================================================

  /**
   * Track chat session started
   */
  trackChatSessionStarted(sessionId: string, source: string) {
    this.logEvent(ANALYTICS_EVENTS.CHAT_SESSION_STARTED, {
      session_id: sessionId,
      source,
      page_location: window.location.pathname,
    });

    // Track funnel: Discovery stage
    const hasStartedChat = localStorage.getItem('has_started_chat');
    if (!hasStartedChat) {
      this.logEvent(ANALYTICS_EVENTS.FUNNEL_CHAT_STARTED, {
        source,
        first_chat: true,
      });
      localStorage.setItem('has_started_chat', 'true');
    }
  }

  /**
   * Track chat message sent
   */
  trackChatMessageSent(sessionId: string, messageLength: number, hasAttachment: boolean) {
    this.logEvent(ANALYTICS_EVENTS.CHAT_MESSAGE_SENT, {
      session_id: sessionId,
      message_length: messageLength,
      has_attachment: hasAttachment,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track the moment the reader first sees what an answer is being built from.
   *
   * Fires once per turn, on the FIRST source only — the rest are noise for
   * this measurement, and `source_count` on the later `chat_response_received`
   * already says how many there were in total.
   */
  trackFirstSourceShown(sessionId: string, elapsedMs: number) {
    this.logEvent(ANALYTICS_EVENTS.CHAT_FIRST_SOURCE_SHOWN, {
      session_id: sessionId,
      elapsed_ms: elapsedMs,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track chat response received (value delivered)
   */
  trackChatResponseReceived(sessionId: string, hasChart: boolean, responseTime: number) {
    this.logEvent(ANALYTICS_EVENTS.CHAT_RESPONSE_RECEIVED, {
      session_id: sessionId,
      has_chart: hasChart,
      response_time_ms: responseTime,
      page_location: window.location.pathname,
    });

    // Track funnel: Engagement stage - AI delivered value
    this.logEvent(ANALYTICS_EVENTS.FUNNEL_AI_ANSWER_RECEIVED, {
      session_id: sessionId,
      has_chart: hasChart,
    });

    if (hasChart) {
      this.logEvent(ANALYTICS_EVENTS.FUNNEL_CHART_GENERATED, {
        session_id: sessionId,
        source: 'chat',
      });
    }
  }

  /**
   * Track chart viewed in chat
   */
  trackChatChartViewed(sessionId: string, chartType: string) {
    this.logEvent(ANALYTICS_EVENTS.CHAT_CHART_VIEWED, {
      session_id: sessionId,
      chart_type: chartType,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track chart exported from chat
   */
  // 'html' is the only chat-export format from 2026-08-22 ([[BUGS #142]]);
  // 'pdf'/'markdown' stay in the union so historical events remain typed,
  // and 'png'/'svg' are the single-chart exports, a different action.
  trackChatChartExported(sessionId: string, format: 'png' | 'svg' | 'html' | 'pdf' | 'markdown') {
    this.logEvent(ANALYTICS_EVENTS.CHAT_CHART_EXPORTED, {
      session_id: sessionId,
      export_format: format,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track file uploaded to chat
   */
  trackChatFileUploaded(sessionId: string, fileType: string, fileSize: number) {
    this.logEvent(ANALYTICS_EVENTS.CHAT_FILE_UPLOADED, {
      session_id: sessionId,
      file_type: fileType,
      file_size_kb: Math.round(fileSize / 1024),
      page_location: window.location.pathname,
    });
  }

  /**
   * Track presentation exported from chat
   */
  trackChatPresentationExported(sessionId: string, slideCount: number) {
    this.logEvent(ANALYTICS_EVENTS.CHAT_PRESENTATION_EXPORTED, {
      session_id: sessionId,
      slide_count: slideCount,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track chat session deleted
   */
  trackChatSessionDeleted(sessionId: string) {
    this.logEvent(ANALYTICS_EVENTS.CHAT_SESSION_DELETED, {
      session_id: sessionId,
      page_location: window.location.pathname,
    });
  }

  // =============================================================================
  // KNOWLEDGE BASE TRACKING METHODS
  // =============================================================================

  /**
   * Track knowledge base document uploaded
   */
  trackKnowledgeBaseUpload(docId: string, fileType: string, fileSize: number) {
    this.logEvent(ANALYTICS_EVENTS.KNOWLEDGE_BASE_UPLOAD, {
      doc_id: docId,
      file_type: fileType,
      file_size_kb: Math.round(fileSize / 1024),
      page_location: window.location.pathname,
    });
  }

  /**
   * Track knowledge base document deleted
   */
  trackKnowledgeBaseDelete(docId: string) {
    this.logEvent(ANALYTICS_EVENTS.KNOWLEDGE_BASE_DELETE, {
      doc_id: docId,
      page_location: window.location.pathname,
    });
  }

  // =============================================================================
  // PORTFOLIO TRACKING METHODS
  // =============================================================================

  /**
   * Track portfolio saved
   */
  trackPortfolioSaved(portfolioId: string, holdingCount: number, isNew: boolean) {
    const eventName = isNew ? ANALYTICS_EVENTS.PORTFOLIO_SAVED : ANALYTICS_EVENTS.PORTFOLIO_UPDATED;
    this.logEvent(eventName, {
      portfolio_id: portfolioId,
      holding_count: holdingCount,
      page_location: window.location.pathname,
    });

    // Track funnel: First save activation
    if (isNew) {
      const hasSaved = localStorage.getItem('has_saved_portfolio');
      if (!hasSaved) {
        this.logEvent(ANALYTICS_EVENTS.FUNNEL_FIRST_SAVE, {
          entity_type: 'portfolio',
          portfolio_id: portfolioId,
        });
        localStorage.setItem('has_saved_portfolio', 'true');
      }
    }
  }

  /**
   * Track portfolio deleted
   */
  trackPortfolioDeleted(portfolioId: string) {
    this.logEvent(ANALYTICS_EVENTS.PORTFOLIO_DELETED, {
      portfolio_id: portfolioId,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track portfolio imported (from file/image)
   */
  trackPortfolioImported(method: 'csv' | 'image' | 'manual', holdingCount: number) {
    this.logEvent(ANALYTICS_EVENTS.PORTFOLIO_IMPORTED, {
      import_method: method,
      holding_count: holdingCount,
      page_location: window.location.pathname,
    });
  }

  // =============================================================================
  // WATCHLIST TRACKING METHODS
  // =============================================================================

  /**
   * Track watchlist created
   */
  trackWatchlistCreated(watchlistId: string, symbolCount: number) {
    this.logEvent(ANALYTICS_EVENTS.WATCHLIST_CREATED, {
      watchlist_id: watchlistId,
      symbol_count: symbolCount,
      page_location: window.location.pathname,
    });

    // Track funnel: First save activation
    const hasSaved = localStorage.getItem('has_saved_watchlist');
    if (!hasSaved) {
      this.logEvent(ANALYTICS_EVENTS.FUNNEL_FIRST_SAVE, {
        entity_type: 'watchlist',
        watchlist_id: watchlistId,
      });
      localStorage.setItem('has_saved_watchlist', 'true');
    }
  }

  /**
   * Track symbol added to watchlist
   */
  trackWatchlistSymbolAdded(watchlistId: string, symbol: string) {
    this.logEvent(ANALYTICS_EVENTS.WATCHLIST_SYMBOL_ADDED, {
      watchlist_id: watchlistId,
      symbol,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track symbol removed from watchlist
   */
  trackWatchlistSymbolRemoved(watchlistId: string, symbol: string) {
    this.logEvent(ANALYTICS_EVENTS.WATCHLIST_SYMBOL_REMOVED, {
      watchlist_id: watchlistId,
      symbol,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track watchlist deleted
   */
  trackWatchlistDeleted(watchlistId: string) {
    this.logEvent(ANALYTICS_EVENTS.WATCHLIST_DELETED, {
      watchlist_id: watchlistId,
      page_location: window.location.pathname,
    });
  }

  // =============================================================================
  // FUNNEL TRACKING METHODS
  // =============================================================================

  /**
   * Track tool discovery (first visit to any tool page)
   */
  trackToolDiscovered(tool: 'stock_research' | 'chat' | 'portfolio_analysis') {
    const toolKey = `discovered_${tool}`;
    const hasDiscovered = localStorage.getItem(toolKey);

    if (!hasDiscovered) {
      this.logEvent(ANALYTICS_EVENTS.FUNNEL_TOOL_DISCOVERED, {
        tool,
        page_location: window.location.pathname,
        referrer: document.referrer,
      });
      localStorage.setItem(toolKey, 'true');
    }
  }

  /**
   * Track return visit (user came back after first visit)
   */
  trackReturnVisit() {
    const visitCount = parseInt(localStorage.getItem('visit_count') || '0', 10) + 1;
    localStorage.setItem('visit_count', visitCount.toString());

    if (visitCount === 2) {
      this.logEvent(ANALYTICS_EVENTS.FUNNEL_RETURN_VISIT, {
        visit_count: visitCount,
        days_since_first: this.getDaysSinceFirstVisit(),
      });
    }
  }

  /**
   * Helper: Get days since first visit
   */
  private getDaysSinceFirstVisit(): number {
    const firstVisit = localStorage.getItem('first_visit_date');
    if (!firstVisit) {
      localStorage.setItem('first_visit_date', new Date().toISOString());
      return 0;
    }
    const days = Math.floor((Date.now() - new Date(firstVisit).getTime()) / (1000 * 60 * 60 * 24));
    return days;
  }

  /**
   * Track value delivered (user received actionable insight). Auto-throttled
   * to once-per-session via sessionStorage so the funnel rollup count
   * matches the number of activated sessions, not the number of value
   * moments. The specific sub-event (chart_generated, ai_answer_received,
   * risk_analysis_complete, etc.) should always fire alongside this.
   */
  trackValueDelivered(valueType: 'chart' | 'ai_answer' | 'risk_analysis' | 'stock_data', context?: Record<string, any>) {
    const SESSION_KEY = 'analytics_value_delivered_fired';
    try {
      if (sessionStorage.getItem(SESSION_KEY) === '1') return;
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      // sessionStorage may be disabled in private browsing — fire once anyway,
      // worst case we double-count for that session.
    }
    this.logEvent(ANALYTICS_EVENTS.FUNNEL_VALUE_DELIVERED, {
      value_type: valueType,
      ...context,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track a click on a landing-page call-to-action. Used to compare
   * conversion of the four "See My Risk" placements against each other
   * and identify dead CTAs that can be removed.
   */
  trackLandingCTA(ctaId: 'see_my_risk' | 'research_a_stock' | 'ask_the_ai' | 'read_latest_letter',
                  ctaPosition: 'hero' | 'after_journey' | 'after_tools' | 'final') {
    this.logEvent('landing_cta_clicked', {
      cta_id: ctaId,
      cta_position: ctaPosition,
      page_location: window.location.pathname,
    });
  }

  // =============================================================================
  // ONBOARDING FUNNEL TRACKING METHODS
  // =============================================================================

  /**
   * Track discovery quiz opened
   */
  trackQuizOpened(source: string) {
    this.logEvent(ANALYTICS_EVENTS.ONBOARDING_QUIZ_OPENED, {
      source,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track quiz step viewed (user sees a question)
   */
  trackQuizStepViewed(step: number, questionId: string) {
    this.logEvent(ANALYTICS_EVENTS.ONBOARDING_QUIZ_STEP_VIEWED, {
      step,
      question_id: questionId,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track quiz step completed (user answers a question)
   */
  trackQuizStepCompleted(step: number, questionId: string, answer: string | string[], timeSpentMs?: number) {
    this.logEvent(ANALYTICS_EVENTS.ONBOARDING_QUIZ_STEP_COMPLETED, {
      step,
      question_id: questionId,
      answer: Array.isArray(answer) ? answer.join(',') : answer,
      time_spent_ms: timeSpentMs,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track quiz completed (all questions answered)
   */
  trackQuizCompleted(answers: Record<string, any>, totalTimeMs?: number) {
    this.logEvent(ANALYTICS_EVENTS.ONBOARDING_QUIZ_COMPLETED, {
      persona: answers.persona,
      goal: answers.goal,
      risk: answers.risk,
      horizon: answers.horizon,
      needs: answers.needs?.join(','),
      total_time_ms: totalTimeMs,
      page_location: window.location.pathname,
    });

    // Mark quiz as completed for funnel tracking
    localStorage.setItem('onboarding_quiz_completed', 'true');
  }

  /**
   * Track quiz abandoned (closed before completion)
   */
  trackQuizAbandoned(step: number, lastQuestionId: string, timeSpentMs?: number) {
    this.logEvent(ANALYTICS_EVENTS.ONBOARDING_QUIZ_ABANDONED, {
      step,
      last_question_id: lastQuestionId,
      time_spent_ms: timeSpentMs,
      completion_percent: Math.round((step / 5) * 100),
      page_location: window.location.pathname,
    });

    // Track as drop-off point
    this.logEvent(ANALYTICS_EVENTS.ONBOARDING_DROPOFF, {
      stage: 'quiz',
      step,
      last_question_id: lastQuestionId,
    });
  }

  /**
   * Track quiz result screen viewed
   */
  trackQuizResultViewed(headline: string, recommendationCount: number) {
    this.logEvent(ANALYTICS_EVENTS.ONBOARDING_QUIZ_RESULT_VIEWED, {
      headline,
      recommendation_count: recommendationCount,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track live insights shown on result screen
   */
  trackQuizInsightsShown(insightTypes: string[], symbols: string[], hasLiveData: boolean) {
    this.logEvent(ANALYTICS_EVENTS.ONBOARDING_QUIZ_INSIGHTS_SHOWN, {
      insight_types: insightTypes.join(','),
      symbols: symbols.join(','),
      has_live_data: hasLiveData,
      insight_count: insightTypes.length,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track insight card clicked
   */
  trackQuizInsightClicked(insightType: string, route: string, requiresLogin: boolean) {
    this.logEvent(ANALYTICS_EVENTS.ONBOARDING_QUIZ_INSIGHT_CLICKED, {
      insight_type: insightType,
      route,
      requires_login: requiresLogin,
      page_location: window.location.pathname,
    });

    if (requiresLogin) {
      this.logEvent(ANALYTICS_EVENTS.ONBOARDING_DROPOFF, {
        stage: 'insight_click_login_required',
        insight_type: insightType,
        route,
      });
    }
  }

  /**
   * Track quiz CTA clicked (Save My Plan, Explore First)
   */
  trackQuizCtaClicked(action: 'save_my_plan' | 'explore_first' | 'recommendation_clicked', route?: string) {
    this.logEvent(ANALYTICS_EVENTS.ONBOARDING_QUIZ_CTA_CLICKED, {
      action,
      route,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track post-login welcome shown
   */
  trackWelcomeShown(route: string, persona: string) {
    this.logEvent(ANALYTICS_EVENTS.ONBOARDING_WELCOME_SHOWN, {
      route,
      persona,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track post-login welcome CTA clicked
   */
  trackWelcomeCtaClicked(action: 'go_to_tool' | 'dismiss', route?: string) {
    this.logEvent(ANALYTICS_EVENTS.ONBOARDING_WELCOME_CTA_CLICKED, {
      action,
      route,
      page_location: window.location.pathname,
    });

    if (action === 'dismiss') {
      this.logEvent(ANALYTICS_EVENTS.ONBOARDING_DROPOFF, {
        stage: 'welcome_dismissed',
      });
    }
  }

  /**
   * Track onboarding task completed (star earned)
   */
  trackOnboardingTaskCompleted(taskId: string, starsEarned: number, totalStars: number) {
    this.logEvent(ANALYTICS_EVENTS.ONBOARDING_TASK_COMPLETED, {
      task_id: taskId,
      stars_earned: starsEarned,
      total_stars: totalStars,
      completion_percent: Math.round((starsEarned / totalStars) * 100),
      page_location: window.location.pathname,
    });

    // Track when all stars are earned
    if (starsEarned >= totalStars) {
      this.logEvent(ANALYTICS_EVENTS.ONBOARDING_ALL_STARS_EARNED, {
        total_stars: totalStars,
        page_location: window.location.pathname,
      });
    }
  }

  /**
   * Track onboarding checklist shown
   */
  trackChecklistShown(starsEarned: number, totalStars: number) {
    this.logEvent(ANALYTICS_EVENTS.ONBOARDING_CHECKLIST_SHOWN, {
      stars_earned: starsEarned,
      total_stars: totalStars,
      completion_percent: Math.round((starsEarned / totalStars) * 100),
      page_location: window.location.pathname,
    });
  }

  /**
   * Track onboarding checklist dismissed
   */
  trackChecklistDismissed(starsEarned: number, totalStars: number) {
    this.logEvent(ANALYTICS_EVENTS.ONBOARDING_CHECKLIST_DISMISSED, {
      stars_earned: starsEarned,
      total_stars: totalStars,
      completion_percent: Math.round((starsEarned / totalStars) * 100),
      page_location: window.location.pathname,
    });

    this.logEvent(ANALYTICS_EVENTS.ONBOARDING_DROPOFF, {
      stage: 'checklist_dismissed',
      stars_earned: starsEarned,
    });
  }

  // =============================================================================
  // MICRO-INTERACTION TRACKING METHODS
  // =============================================================================

  /**
   * Track module/section expanded (accordion, collapsible sections)
   */
  trackModuleExpanded(moduleName: string, page: string) {
    this.logEvent(ANALYTICS_EVENTS.MODULE_EXPANDED, {
      module_name: moduleName,
      page,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track module/section collapsed
   */
  trackModuleCollapsed(moduleName: string, page: string) {
    this.logEvent(ANALYTICS_EVENTS.MODULE_COLLAPSED, {
      module_name: moduleName,
      page,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track tab selection within a page
   */
  trackTabSelected(tabName: string, page: string) {
    this.logEvent(ANALYTICS_EVENTS.TAB_SELECTED, {
      tab_name: tabName,
      page,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track dropdown/select value changes
   */
  trackDropdownSelected(dropdownName: string, value: string, page: string) {
    this.logEvent(ANALYTICS_EVENTS.DROPDOWN_SELECTED, {
      dropdown_name: dropdownName,
      selected_value: value,
      page,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track chart interactions (hover, zoom, pan)
   */
  trackChartInteraction(chartType: string, interactionType: 'hover' | 'zoom' | 'pan' | 'click', page: string) {
    this.logEvent(ANALYTICS_EVENTS.CHART_INTERACTION, {
      chart_type: chartType,
      interaction_type: interactionType,
      page,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track table sorting
   */
  trackTableSorted(columnName: string, direction: 'asc' | 'desc', page: string) {
    this.logEvent(ANALYTICS_EVENTS.TABLE_SORTED, {
      column_name: columnName,
      sort_direction: direction,
      page,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track table filtering
   */
  trackTableFiltered(filterType: string, filterValue: string, page: string) {
    this.logEvent(ANALYTICS_EVENTS.TABLE_FILTERED, {
      filter_type: filterType,
      filter_value: filterValue,
      page,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track modal/dialog opened
   */
  trackModalOpened(modalName: string, page: string) {
    this.logEvent(ANALYTICS_EVENTS.MODAL_OPENED, {
      modal_name: modalName,
      page,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track modal/dialog closed
   */
  trackModalClosed(modalName: string, action: 'completed' | 'cancelled' | 'dismissed', page: string) {
    this.logEvent(ANALYTICS_EVENTS.MODAL_CLOSED, {
      modal_name: modalName,
      close_action: action,
      page,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track quick actions (chip clicks, quick links, popular stock clicks, etc.)
   */
  trackQuickAction(actionType: string, value: string, page: string) {
    this.logEvent(ANALYTICS_EVENTS.QUICK_ACTION, {
      action_type: actionType,
      action_value: value,
      page,
      page_location: window.location.pathname,
    });
  }

  /**
   * Track page engagement (time spent, interactions count)
   */
  trackPageEngagement(page: string, engagementType: 'view' | 'active' | 'idle' = 'view') {
    this.logEvent(ANALYTICS_EVENTS.PAGE_ENGAGEMENT, {
      page,
      engagement_type: engagementType,
      page_location: window.location.pathname,
    });
  }

  // =============================================================================
  // RISK REPORT TRACKING METHODS
  // =============================================================================

  trackRiskReportSaved(reportId: string, holdingsCount: number, riskScore: number) {
    this.logEvent(ANALYTICS_EVENTS.RISK_REPORT_SAVED, {
      report_id: reportId,
      holdings_count: holdingsCount,
      risk_score: riskScore,
      page_location: window.location.pathname,
    });
  }

  trackRiskReportLoaded(reportId: string) {
    this.logEvent(ANALYTICS_EVENTS.RISK_REPORT_LOADED, {
      report_id: reportId,
      page_location: window.location.pathname,
    });
  }

  trackRiskReportDeleted(reportId: string) {
    this.logEvent(ANALYTICS_EVENTS.RISK_REPORT_DELETED, {
      report_id: reportId,
      page_location: window.location.pathname,
    });
  }

  // ============================================================================
  // Level-Up Gamification Events
  // ============================================================================

  trackLevelUpQuizStarted(quizId: string, difficulty: string) {
    this.logEvent(ANALYTICS_EVENTS.LEVEL_UP_QUIZ_STARTED, {
      quiz_id: quizId,
      difficulty,
    });
  }

  trackLevelUpQuizCompleted(quizId: string, score: number, totalQuestions: number, passed: boolean) {
    const event = passed ? ANALYTICS_EVENTS.LEVEL_UP_QUIZ_PASSED : ANALYTICS_EVENTS.LEVEL_UP_QUIZ_FAILED;
    this.logEvent(event, {
      quiz_id: quizId,
      score,
      total_questions: totalQuestions,
      score_percent: Math.round((score / totalQuestions) * 100),
    });
    this.logEvent(ANALYTICS_EVENTS.LEVEL_UP_QUIZ_COMPLETED, {
      quiz_id: quizId,
      passed,
      score_percent: Math.round((score / totalQuestions) * 100),
    });
  }

  trackLevelUpChallengeStarted() {
    this.logEvent(ANALYTICS_EVENTS.LEVEL_UP_CHALLENGE_STARTED, {});
  }

  trackLevelUpChallengeCompleted(passed: boolean) {
    this.logEvent(ANALYTICS_EVENTS.LEVEL_UP_CHALLENGE_COMPLETED, { passed });
  }

  trackLevelAchieved(level: number) {
    this.logEvent(ANALYTICS_EVENTS.LEVEL_UP_LEVEL_ACHIEVED, { level });
  }

  trackNotificationOpened() {
    this.logEvent(ANALYTICS_EVENTS.NOTIFICATION_OPENED, {});
  }

  trackNotificationRead(notificationId: string, type: string) {
    this.logEvent(ANALYTICS_EVENTS.NOTIFICATION_READ, {
      notification_id: notificationId,
      notification_type: type,
    });
  }

  // ==========================================================================
  // ACTIVATION & RETENTION SIGNALS (2026-06-13 — P0 audit recommendations)
  //
  // "Activation" is the single most-predictive retention metric in consumer
  // SaaS: when a user crosses the threshold from "tried it" to "got real
  // value." Each activation event fires AT MOST ONCE per user (guarded by
  // localStorage flag) so cohort analysis in BigQuery / Firebase Funnels can
  // count first-time activations cleanly.
  //
  // **User-property cap discipline (Firebase Analytics: 25 props/project)**:
  // Rather than emit N×3 properties (one per activation type), we keep a
  // single CSV `activations_completed` plus 3 fixed first-activation props.
  // Total = 4 user properties regardless of how many activation types exist.
  // ==========================================================================

  /**
   * Track a canonical "aha moment" — the user crossed from trial to value.
   *
   * @param activationType - Which specific activation moment this is
   * @param metadata - Activation-specific context (holding count, chart type, etc.)
   */
  trackActivation(
    activationType: ActivationType,
    metadata?: Record<string, any>,
  ) {
    const storageKey = `activation_${activationType}_fired`;
    try {
      if (localStorage.getItem(storageKey)) {
        return; // Already fired for this user; no-op
      }
    } catch {
      // localStorage unavailable — log anyway, drop the dedup
    }

    const firstVisitTs = this._readFirstVisitTimestamp();
    const hoursToActivation = firstVisitTs !== null
      ? Math.round((Date.now() - firstVisitTs) / (1000 * 60 * 60))
      : null;

    this.logEvent('activation', {
      activation_type: activationType,
      hours_to_activation: hoursToActivation,
      page_location: window.location.pathname,
      ...metadata,
    });

    // **Critical ordering**: resolve isFirstActivation ONCE, write localStorage
    // BEFORE setUserProperties so a thrown setItem cannot leave the user with
    // user-property state that disagrees with localStorage on subsequent
    // sessions. (Reviewer caught TOCTOU + write-after-emit race.)
    const isFirst = this._isFirstActivation();
    const completedList = this._appendActivationToCsv(activationType);

    try {
      localStorage.setItem(storageKey, '1');
      if (isFirst) {
        localStorage.setItem('first_activation_at', String(Date.now()));
        localStorage.setItem('first_activation_type', activationType);
      }
    } catch {
      // Ignore — analytics emission below still runs
    }

    // Consolidated user properties: 1 CSV + 3 first-activation props (set once).
    this.setUserProperties({
      activations_completed: completedList,
      ...(isFirst ? {
        first_activation_type: activationType,
        activated_at: new Date().toISOString().slice(0, 10),
        time_to_activation_hours: hoursToActivation,
      } : {}),
    });
  }

  private _isFirstActivation(): boolean {
    try {
      return !localStorage.getItem('first_activation_at');
    } catch {
      return true;
    }
  }

  private _readFirstVisitTimestamp(): number | null {
    try {
      const raw = localStorage.getItem('first_visit_timestamp');
      return raw ? Number(raw) : null;
    } catch {
      return null;
    }
  }

  /** Read+append a CSV of completed activations, dedup'd. */
  private _appendActivationToCsv(activationType: ActivationType): string {
    return this._appendToCsvSet('activations_completed_csv', activationType);
  }

  /** Read+append a CSV of habit-formed features, dedup'd. */
  private _appendHabitToCsv(feature: FeatureName): string {
    return this._appendToCsvSet('habits_formed_csv', feature);
  }

  /** Generic CSV-set append helper. localStorage-backed, dedup'd via Set.
   *  Used by activation + habit tracking to keep user-property count flat. */
  private _appendToCsvSet(storageKey: string, value: string): string {
    try {
      const existing = localStorage.getItem(storageKey) || '';
      const set = new Set(existing.split(',').filter(Boolean));
      set.add(value);
      const next = Array.from(set).join(',');
      localStorage.setItem(storageKey, next);
      return next;
    } catch {
      return value;
    }
  }

  // ==========================================================================
  // DYNAMIC USER-PROPERTY REFRESH (P0/P1 audit)
  //
  // setUserProperties at signup only is insufficient — user state changes
  // (subscription_tier ramps, portfolio count grows, level_up tier advances).
  // refreshUserProperties pulls current state from app stores + Firestore and
  // re-sets user properties so cohort segments stay accurate over time.
  //
  // Call sites:
  //   1. AuthContext: after credit-store sync (subscription_tier becomes accurate)
  //   2. On every session start (page mount once)
  //   3. After CRUD that changes count signals (portfolio save/delete, watchlist add)
  // ==========================================================================
  /**
   * Keys are snake_case (GA4 convention). The mapping from any camelCase
   * store fields to these snake_case keys is the caller's responsibility —
   * keeps this service free of inline regex transforms and makes the
   * surface explicit at every call site.
   *
   * `last_active_at` defaults to today when absent.
   */
  refreshUserProperties(state: UserPropertiesRefresh) {
    const filtered: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(state)) {
      if (value !== undefined && value !== null) filtered[key] = value;
    }
    if (!filtered.last_active_at) {
      filtered.last_active_at = new Date().toISOString().slice(0, 10);
    }
    this.setUserProperties(filtered);
  }

  // ==========================================================================
  // TIME-ON-PAGE TRACKING (P1 audit)
  //
  // Distinguishes browsers from real users. Fired on page-exit via
  // beforeunload / visibility-change. `usePageEngagement` hook handles the
  // listener lifecycle; this is just the analytics emission.
  // ==========================================================================
  /**
   * Fires the existing `page_engagement` event with `phase: 'exit'` so
   * BigQuery can distinguish view-pings (single fire on mount) from
   * exit-pings (with `time_on_page_seconds` + `engagement_depth`). The
   * existing view-ping call sites should be migrated to `phase: 'view'`
   * over time; until then `phase` defaults to `'exit'` here because the
   * other shape (view-only) carries no time field.
   */
  trackTimeOnPage(
    pageName: string,
    timeOnPageSeconds: number,
    engagementDepth: 'shallow' | 'medium' | 'deep' = 'shallow',
    metadata?: Record<string, any>,
  ) {
    this.logEvent(ANALYTICS_EVENTS.PAGE_ENGAGEMENT, {
      phase: 'exit',
      page_name: pageName,
      time_on_page_seconds: Math.round(timeOnPageSeconds),
      engagement_depth: engagementDepth,
      page_location: window.location.pathname,
      ...metadata,
    });
  }

  // ==========================================================================
  // SCROLL DEPTH (P2 audit)
  //
  // Fires at 25/50/75/100% scroll milestones — used on long-form content
  // surfaces (academy, FAQ, stock research detail) to distinguish "viewed"
  // from "engaged." `useScrollDepth` hook handles the listener.
  // ==========================================================================
  trackScrollDepth(pageName: string, depthPercent: 25 | 50 | 75 | 100) {
    this.logEvent(ANALYTICS_EVENTS.SCROLL_DEPTH, {
      page_name: pageName,
      depth_percent: depthPercent,
      page_location: window.location.pathname,
    });
  }

  // ==========================================================================
  // FEATURE-LEVEL MICRO-FUNNELS (P1 audit)
  //
  // The macro signup funnel (FUNNEL_*) tracks marketing → activation. These
  // micro-funnels track WITHIN each tool — where does the user disengage?
  //
  // Step names are intentionally human-readable so Firebase Funnel reports
  // are self-documenting.
  // ==========================================================================
  trackResearchFunnel(
    step:
      | 'search_initiated'
      | 'symbol_result_clicked'
      | 'metrics_section_expanded'
      | 'news_section_viewed'
      | 'added_to_watchlist'
      | 'compared_to_peer',
    metadata?: Record<string, any>,
  ) {
    this.logEvent(`research_funnel_${step}`, {
      page_location: window.location.pathname,
      ...metadata,
    });
  }

  trackPortfolioFunnel(
    step:
      | 'holdings_entered'
      | 'reconcile_passed'
      | 'analyze_clicked'
      | 'report_viewed'
      | 'portfolio_saved'
      | 'report_shared',
    metadata?: Record<string, any>,
  ) {
    this.logEvent(`portfolio_funnel_${step}`, {
      page_location: window.location.pathname,
      ...metadata,
    });
  }

  trackChatFunnel(
    step:
      | 'session_started'
      | 'first_message_sent'
      | 'first_response_received'
      | 'follow_up_sent'
      | 'chart_received'
      | 'export_triggered'
      // A signed-out reader's question, sent for them right after sign-in
      // (ROADMAP 0.11 S2 — the gate's conversion event).
      | 'question_sent_after_sign_in',
    metadata?: Record<string, any>,
  ) {
    this.logEvent(`chat_funnel_${step}`, {
      page_location: window.location.pathname,
      ...metadata,
    });
  }

  // ==========================================================================
  // FEATURE REPEAT USE (P2 audit) — habit-forming signal
  //
  // Tracks per-feature `total_uses_count` and `days_since_first_use`. The
  // cohort of users with `total_uses_count >= 3` within 14 days is the
  // habit-forming retention base — far stronger than "did they sign up."
  //
  // **User-property cap discipline**: rather than one boolean per feature
  // (`habit_formed_<feature>`), we keep a single CSV `habits_formed` so the
  // total user-property cost is 1 regardless of feature count.
  // **Feature names are a closed `FeatureName` union** so a typo or
  // ad-hoc string at a call site can't silently inflate the cardinality.
  // ==========================================================================
  trackFeatureRepeatUse(feature: FeatureName, metadata?: Record<string, any>) {
    const firstUseKey = `feature_first_use_${feature}`;
    const countKey = `feature_use_count_${feature}`;
    let firstUseTimestamp: number = Date.now();
    let useCount = 1;
    try {
      const existing = localStorage.getItem(firstUseKey);
      if (existing) {
        firstUseTimestamp = Number(existing);
      } else {
        localStorage.setItem(firstUseKey, String(firstUseTimestamp));
      }
      useCount = Number(localStorage.getItem(countKey) || '0') + 1;
      localStorage.setItem(countKey, String(useCount));
    } catch {
      // localStorage unavailable — fall back to in-memory defaults. The
      // habit_formed flag won't graduate without persistence, which is
      // the correct behaviour (the user has no persistent retention signal).
    }
    const daysSinceFirstUse = Math.floor((Date.now() - firstUseTimestamp) / 86400000);

    this.logEvent('feature_repeat_use', {
      feature,
      total_uses_count: useCount,
      days_since_first_use: daysSinceFirstUse,
      is_first_use: useCount === 1,
      page_location: window.location.pathname,
      ...metadata,
    });

    // Habit-formed cohort: 3+ uses within 14 days. Append to the consolidated
    // CSV user property (no N×features explosion against the 25-prop cap).
    if (useCount >= 3 && daysSinceFirstUse <= 14) {
      const habits = this._appendHabitToCsv(feature);
      this.setUserProperties({ habits_formed: habits });
    }
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

// Bootstrap: stamp first-visit timestamp once per user (used for activation
// hours_to_activation calculation later). Wrapped in try/catch because
// localStorage may be unavailable in some embedded contexts.
try {
  if (typeof window !== 'undefined' && !localStorage.getItem('first_visit_timestamp')) {
    localStorage.setItem('first_visit_timestamp', String(Date.now()));
  }
} catch {
  // Ignore
}

export default analyticsService;